import type { Config } from '@backstage/config';

/**
 * Frontend role resolver — mirrors the backend `RoleResolver` so the UI
 * shows/hides controls consistently with what the backend permission
 * policy actually enforces.
 *
 * Reads the same `permission.rbac.*` config keys as the backend (set in
 * the base `app-config.yaml`). Adding a new module/cohort requires no
 * code change — just edit app-config and create the matching Keycloak
 * group + System entity.
 *
 * Important: this is a UI-convenience layer only. Authoritative
 * authorization is enforced server-side by the catalog permission
 * policy. Don't rely on these checks for security.
 */
export class RoleResolver {
  constructor(private readonly config: Config) {}

  private privilegedGroupNames(): string[] {
    return this.config.getOptionalStringArray('permission.rbac.privilegedGroups') ?? [];
  }

  private umbrellaReaderGroupNames(): string[] {
    return this.config.getOptionalStringArray('permission.rbac.umbrellaReaderGroups') ?? [];
  }

  private scopeWideReaderGroupNames(): string[] {
    return this.config.getOptionalStringArray('permission.rbac.scopeWideReaderGroups') ?? [];
  }

  private toRefs(names: string[]): string[] {
    return names.map(n => `group:default/${n}`);
  }

  /** True if the user is in any privilegedGroups entry. */
  isAdmin(refs: string[]): boolean {
    return this.toRefs(this.privilegedGroupNames()).some(r => refs.includes(r));
  }

  /** True if the user is in any umbrellaReaderGroups entry (e.g. teachers). */
  isUmbrellaReader(refs: string[]): boolean {
    return this.toRefs(this.umbrellaReaderGroupNames()).some(r => refs.includes(r));
  }

  /** True if the user is in any scopeWideReaderGroups entry (e.g. eecs-research). */
  isScopeWideReader(refs: string[]): boolean {
    return this.toRefs(this.scopeWideReaderGroupNames()).some(r => refs.includes(r));
  }

  /**
   * Has any role above plain-student (admin OR umbrella reader OR
   * scope-wide reader).
   */
  isStaff(refs: string[]): boolean {
    return this.isAdmin(refs) || this.isUmbrellaReader(refs) || this.isScopeWideReader(refs);
  }

  /**
   * Plain student: has at least one module group, but no admin/umbrella
   * /scope-wide-reader role.
   */
  isStudent(refs: string[]): boolean {
    if (this.isStaff(refs)) {
      return false;
    }
    const privileged = this.toRefs(this.privilegedGroupNames());
    const umbrella = this.toRefs(this.umbrellaReaderGroupNames());
    const moduleGroups = refs
      .filter(r => r.startsWith('group:default/'))
      .filter(r => !privileged.includes(r))
      .filter(r => !umbrella.includes(r));
    return moduleGroups.length > 0;
  }
}

// ---------------------------------------------------------------------------
// Backwards-compatible function wrappers.
//
// All existing call sites pass an extra `config` argument. The wrappers
// instantiate a fresh resolver per call — fine for click-handler cadence;
// for hot paths prefer `new RoleResolver(config)` once and reuse.
// ---------------------------------------------------------------------------

export const isAdminRole = (refs: string[], config: Config): boolean =>
  new RoleResolver(config).isAdmin(refs);

export const isTeacherRole = (refs: string[], config: Config): boolean =>
  new RoleResolver(config).isUmbrellaReader(refs);

export const isResearchRole = (refs: string[], config: Config): boolean =>
  new RoleResolver(config).isScopeWideReader(refs);

export const isStudentRole = (refs: string[], config: Config): boolean =>
  new RoleResolver(config).isStudent(refs);
